import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { UserCog, Save, Trash2, Landmark } from "lucide-react";
import ConfirmDialog from "../components/ConfirmDialog";

export default function WorkerQuickEditDialog({ entry, projects, onClose, onSave, onDelete }) {
  const open = !!entry;
  const [projectId, setProjectId] = useState("none");
  const [salary, setSalary] = useState("");
  const [bankName, setBankName] = useState("");
  const [bankAccount, setBankAccount] = useState("");
  const [beneficiary, setBeneficiary] = useState("");
  const [phone, setPhone] = useState("");
  const [confirmDel, setConfirmDel] = useState(false);

  useEffect(() => {
    if (entry) {
      setProjectId(entry.project_id || "none");
      setSalary(entry.weekly_salary ?? "");
      setBankName(entry.bank_name || "");
      setBankAccount(entry.bank_account || "");
      setBeneficiary(entry.bank_beneficiary || "");
      setPhone(entry.phone || "");
    }
  }, [entry]);

  const save = async () => {
    await onSave(entry.worker_id, {
      name: entry.worker_name,
      project_id: projectId === "none" ? null : projectId,
      weekly_salary: Math.max(0, Number(salary) || 0),
      notes: entry.notes || "",
      bank_name: bankName,
      bank_account: bankAccount,
      bank_beneficiary: beneficiary,
      phone,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-sm" data-testid="worker-quick-edit">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight flex items-center gap-2">
            <UserCog size={18} /> {entry?.worker_name}
          </DialogTitle>
          <DialogDescription>Mueve al trabajador de obra, ajusta su sueldo, registra sus datos bancarios o elimínalo.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Obra / Proyecto</Label>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger className="h-9 mt-1" data-testid="quick-project"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin obra</SelectItem>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Sueldo semanal</Label>
            <Input type="number" min="0" value={salary} onChange={(e) => setSalary(e.target.value)} data-testid="quick-salary" className="h-9 mt-1 font-mono-data" />
          </div>
          <div className="rounded-sm border border-blue-100 bg-blue-50/50 p-3 space-y-2">
            <p className="text-xs font-semibold text-blue-800 flex items-center gap-1.5"><Landmark size={14} /> Datos bancarios para transferencia</p>
            <div>
              <Label className="text-xs">Banco</Label>
              <Input value={bankName} onChange={(e) => setBankName(e.target.value)} placeholder="Ej. BBVA, Banorte, Santander" data-testid="quick-bank-name" className="h-9 mt-1 bg-white" />
            </div>
            <div>
              <Label className="text-xs">Número de cuenta / CLABE</Label>
              <Input value={bankAccount} onChange={(e) => setBankAccount(e.target.value)} placeholder="Cuenta o CLABE de 18 dígitos" data-testid="quick-bank-account" className="h-9 mt-1 bg-white font-mono-data" />
            </div>
            <div>
              <Label className="text-xs">Nombre del beneficiario (si la cuenta no es del trabajador)</Label>
              <Input value={beneficiary} onChange={(e) => setBeneficiary(e.target.value)} placeholder="Ej. María López (esposa)" data-testid="quick-bank-beneficiary" className="h-9 mt-1 bg-white" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Teléfono</Label>
            <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="10 dígitos" data-testid="quick-phone" className="h-9 mt-1 font-mono-data" />
          </div>
        </div>

        <div className="flex items-center justify-between pt-2">
          <Button variant="ghost" onClick={() => setConfirmDel(true)} data-testid="quick-delete" className="text-red-600 hover:text-red-700 hover:bg-red-50">
            <Trash2 size={15} className="mr-1.5" /> Eliminar
          </Button>
          <Button onClick={save} data-testid="quick-save" className="bg-blue-600 hover:bg-blue-700">
            <Save size={15} className="mr-1.5" /> Guardar cambios
          </Button>
        </div>

        <ConfirmDialog open={confirmDel} onOpenChange={setConfirmDel} testid="confirm-quick-delete"
          title="Eliminar trabajador"
          description="Se eliminará de la semana actual. Las semanas anteriores conservan su historial."
          onConfirm={async () => { setConfirmDel(false); await onDelete(entry.worker_id); }} />
      </DialogContent>
    </Dialog>
  );
}
